import type { AlertRow, AlertType } from '../types';

const REASONS: Record<AlertType, (a: AlertRow) => string> = {
  stalled_start: (a) =>
    `Salesforce shows the engagement started ${a.daysSinceStart ?? '—'} days ago but no coaching sessions have been logged yet. Wanted to check in — has work begun, or is there something blocking the kickoff?`,
  near_completion: (a) =>
    `Salesforce shows ${a.loggedHours} of ${a.contractedHours ?? '—'} contracted hours used (${Math.round(((a.pctComplete ?? 0) * 100))}%). Wanted to flag this before we run out of budget — let me know if a renewal conversation is in order.`,
  stale_activity: () =>
    `The SOW end date is approaching (or has passed) and the Coaching.com engagement is still showing Active. Wanted to confirm whether sessions are still scheduled, or if it's time to wrap this engagement up.`,
  stale_activity_30d: (a) =>
    `Salesforce shows no coaching session logged in ${a.daysSinceLastSession ?? '30+'} days, though the engagement is still active. Wanted to check whether sessions are still happening — and remind you to log any completed hours in Coaching.com.`,
};

/**
 * Builds a `mailto:` URL pre-populated with subject + body about a specific
 * alert. Clicking opens the user's default mail client; they can edit and
 * send themselves. We deliberately don't go through Salesforce — Danielle
 * said the goal is just to draft something she can copy or send herself.
 *
 * Returns null when there's no affiliate email on file, so the caller can
 * hide the button rather than open a useless empty mailto.
 */
export function buildAlertMailto(a: AlertRow): string | null {
  const primary = a.deliveringAffiliates?.[0];
  if (!primary?.email) return null;

  const subject = `${a.accountName ?? 'Engagement'} — ${a.opportunityName ?? 'coaching engagement'}`;
  const greeting = primary.name ? `Hi ${primary.name.split(' ')[0]},\n\n` : 'Hi,\n\n';
  const reason = REASONS[a.alertType](a);
  const sfLink = `\n\nSalesforce: https://thearielgroup.my.salesforce.com/${a.opportunityId}`;
  const sign = `\n\nThanks,\nAriel team`;
  const body = `${greeting}${reason}${sfLink}${sign}`;

  return `mailto:${encodeURIComponent(primary.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
